import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useClickOutside } from 'hooks/useClickOutside'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import { createPortal } from 'react-dom'

import s from './Collections.module.css'

import CollectionsList from './components/CollectionsList/CollectionsList'
import CollectionsCreate from './components/CollectionsCreate/CollectionsCreate'
import CreatePopup from './components/CollectionsCreate/components/CreatePopup/CreatePopup'

const SEARCH_TIMEOUT = 500

function Collections({ onClose, unitId }) {
  const [inputValue, setInputValue] = useState('')
  const [searchResult, setSearchResult] = useState('')
  const [showCreatePopup, setShowCreatePopup] = useState(false)

  const debouncedSearch = useDebouncedCallback((value) => {
    setSearchResult(value)
  }, SEARCH_TIMEOUT)

  const handleInputChange = (value) => {
    setInputValue(value)
    debouncedSearch(value)
  }

  const [collections, setCollections] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  async function fetchCollections() {
    try {
      setIsLoading(true)
      const apiToken = localStorage.getItem('apiToken')

      const response = await fetch('/api/v3/listing_collections', {
        headers: {
          'API_TOKEN': apiToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      })

      const data = await response.json()

      setCollections(data)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCollections()
  }, [])

  useEffect(() => {
    const close = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keyup', close)
    return () => document.removeEventListener('keyup', close)
  }, [])

  const rootRef = useRef(null)

  useClickOutside(rootRef, () => onClose())

  const filteredCollection = useMemo(() => {
    return collections.filter((collection) => collection.name.toLowerCase().includes(searchResult.toLowerCase()))
  }, [searchResult, collections])

  useEffect(() => {
    const activeCollections = collections.filter((collection) => collection.archived === false)
    if (!isLoading && activeCollections.length === 0) {
      setShowCreatePopup(true)
    }
  }, [isLoading, collections, setShowCreatePopup])

  return createPortal(
    <>
      <div className={s.overlay} />
      <div className={s.root} ref={rootRef}>
        <div className={s.title}>Save to Collection</div>

        <CollectionsCreate onCreate={() => setShowCreatePopup(true)} />

        <CollectionsList
          isLoading={isLoading}
          collections={filteredCollection}
          fetchCollections={fetchCollections}
          unitId={unitId}
          searchValue={inputValue}
          onChangeSearchValue={handleInputChange}
        />

        <svg
          className={s.close}
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          onClick={() => onClose()}
        >
          <path
            d="M22.4946 21.2564C22.5758 21.3377 22.6403 21.4342 22.6843 21.5405C22.7283 21.6467 22.751 21.7605 22.751 21.8755C22.751 21.9905 22.7283 22.1043 22.6843 22.2105C22.6403 22.3167 22.5758 22.4133 22.4946 22.4946C22.4133 22.5758 22.3167 22.6403 22.2105 22.6843C22.1043 22.7283 21.9905 22.751 21.8755 22.751C21.7605 22.751 21.6467 22.7283 21.5405 22.6843C21.4342 22.6403 21.3377 22.5758 21.2564 22.4946L14.0005 15.2375L6.74455 22.4946C6.58036 22.6587 6.35768 22.751 6.12549 22.751C5.89329 22.751 5.67061 22.6587 5.50642 22.4946C5.34224 22.3304 5.25 22.1077 5.25 21.8755C5.25 21.6433 5.34224 21.4206 5.50642 21.2564L12.7635 14.0005L5.50642 6.74455C5.34224 6.58036 5.25 6.35768 5.25 6.12549C5.25 5.89329 5.34224 5.67061 5.50642 5.50642C5.67061 5.34224 5.89329 5.25 6.12549 5.25C6.35768 5.25 6.58036 5.34224 6.74455 5.50642L14.0005 12.7635L21.2564 5.50642C21.4206 5.34224 21.6433 5.25 21.8755 5.25C22.1077 5.25 22.3304 5.34224 22.4946 5.50642C22.6587 5.67061 22.751 5.89329 22.751 6.12549C22.751 6.35768 22.6587 6.58036 22.4946 6.74455L15.2375 14.0005L22.4946 21.2564Z"
            fill="currentColor"
          />
        </svg>

        {showCreatePopup && (
          <CreatePopup fetchCollections={fetchCollections} onClose={() => setShowCreatePopup(false)} />
        )}
      </div>
    </>,
    document.body
  )
}

export default Collections
